import { clearToken, getToken, setToken } from "./token";
import type {
  AuthConfigResponse,
  ActivateSubscriptionRequest,
  AddMemberRequest,
  AddPaymentMethodRequest,
  AlertSort,
  ApiErrorBody,
  ApplyCouponRequest,
  BillingConfigResponse,
  BillingSummaryResponse,
  CameraAlertResponse,
  CameraAlertsListResponse,
  CameraResponse,
  CamerasListResponse,
  ClipTokenResponse,
  CouponPreviewResponse,
  CreateEdgeAgentRequest,
  CreateStoreRequest,
  DeviceOrderPreviewResponse,
  DeviceOrderRequest,
  DeviceOrderResponse,
  DeviceOrdersListResponse,
  DeviceReturnResponse,
  EdgeAgentResponse,
  EdgeAgentsListResponse,
  GrantResponse,
  GrantsListResponse,
  InvoiceResponse,
  InvoicesListResponse,
  LoginRequest,
  LoginResponse,
  MediaConsentResponse,
  MemberResponse,
  MembersListResponse,
  MotionPeriodResponse,
  MotionPeriodsListResponse,
  NotificationChannel,
  NvrResponse,
  NvrsListResponse,
  PartnerAttributionsListResponse,
  PartnerContractResponse,
  PartnerStatementResponse,
  PartnerStatusResponse,
  PaymentMethodResponse,
  PaymentMethodsListResponse,
  PhoneResponse,
  PhoneRowResponse,
  PhoneVerificationResponse,
  PhonesListResponse,
  PlansListResponse,
  PrepayPreviewResponse,
  PrepayPurchaseResponse,
  PrepayRequest,
  ProvisionedEdgeAgentResponse,
  RegisterNvrRequest,
  RegisterRequest,
  ReportCsvColumn,
  ReportFilters,
  ReportResponse,
  SalesCodeResolveResponse,
  SensoryLogResponse,
  SensoryLogsListResponse,
  SetupIntentResponse,
  StoreResponse,
  StoresListResponse,
  SubmitWatchInstructionsRequest,
  SubscriptionResponse,
  TelegramLinkCodeResponse,
  TimeRange,
  TransferOwnershipRequest,
  UpdateCameraRequest,
  UpdateEdgeAgentRequest,
  UpdateMeRequest,
  UpdateMemberRoleRequest,
  UpdateNvrRequest,
  UpdateStoreRequest,
  UserResponse,
  ViewTokenResponse,
  WalletPayoutResponse,
  WalletResponse,
  WatchInstructionsResponse,
  WatchItemsResponse,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Skip attaching the Authorization header (used by login/register). */
  skipAuth?: boolean;
};

let onUnauthorized: (() => void) | null = null;

// Registered by the auth context so an authenticated request that 401s (expired
// token) drops the session and redirects to login, instead of failing silently.
export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

const request = async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const { method = "GET", body, skipAuth = false } = opts;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (!skipAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (resp.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await resp.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!resp.ok) {
    // 401 clears the token and (for authenticated calls) tells the auth context to
    // drop the session so RequireAuth redirects to /login. Login/register use
    // skipAuth, so a bad-password 401 there shows its own error without a redirect.
    if (resp.status === 401) {
      clearToken();
      if (!skipAuth) onUnauthorized?.();
    }

    const errBody = (parsed as ApiErrorBody | null) ?? null;
    const message =
      errBody?.error ?? validationMessage(errBody?.errors) ?? `Request failed (${resp.status})`;
    throw new ApiError(resp.status, message, errBody);
  }

  return parsed as T;
};

// Pydantic 400s arrive as { errors: [{ loc, msg, type }] } (see app.py's ValidationError
// handler), not { error } — surface the first message instead of "Request failed (400)".
const validationMessage = (errors: unknown): string | null => {
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first = errors[0] as { loc?: unknown; msg?: unknown };
  if (typeof first.msg !== "string") return null;
  const msg = first.msg.replace(/^Value error, /, "");
  const field = Array.isArray(first.loc)
    ? first.loc.filter((p) => typeof p === "string").at(-1)
    : null;
  return field ? `${field}: ${msg}` : msg;
};

// Either a preset range or an explicit ISO 8601 from/to window.
export type EventWindow = { range?: TimeRange; from?: string; to?: string };

const eventQuery = (opts: EventWindow & { sort?: AlertSort; periodId?: string }): string => {
  const params = new URLSearchParams();
  if (opts.range) params.set("range", opts.range);
  if (opts.from) params.set("from", opts.from);
  if (opts.to) params.set("to", opts.to);
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.periodId) params.set("period_id", opts.periodId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const reportQuery = (filters: ReportFilters, extra: Record<string, string> = {}): string => {
  const params = new URLSearchParams({ from: filters.from, to: filters.to });
  for (const camera of filters.cameras) params.append("camera", camera);
  if (filters.minSeverity !== null) params.set("min_severity", String(filters.minSeverity));
  if (filters.alert) params.set("alert", filters.alert);
  if (filters.category) params.set("category", filters.category);
  if (filters.q.trim()) params.set("q", filters.q.trim());
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  return params.toString();
};

export const api = {
  // --- billing (wizard activation; PRD PAYWALL §4, item 4b) ---
  async getBillingConfig(): Promise<BillingConfigResponse> {
    return request<BillingConfigResponse>("/api/billing/config");
  },

  async getBillingPlans(): Promise<PlansListResponse> {
    return request<PlansListResponse>("/api/billing/plans");
  },

  async getBillingSummary(storeId: string): Promise<BillingSummaryResponse> {
    return request<BillingSummaryResponse>(`/api/stores/${storeId}/billing`);
  },

  async createBillingSetupIntent(storeId: string): Promise<SetupIntentResponse> {
    return request<SetupIntentResponse>(`/api/stores/${storeId}/billing/setup-intent`, {
      method: "POST",
    });
  },

  async listPaymentMethods(storeId: string): Promise<PaymentMethodResponse[]> {
    const resp = await request<PaymentMethodsListResponse>(
      `/api/stores/${storeId}/billing/payment-methods`,
    );
    return resp.payment_methods ?? [];
  },

  async addPaymentMethod(
    storeId: string,
    payload: AddPaymentMethodRequest,
  ): Promise<PaymentMethodResponse> {
    return request<PaymentMethodResponse>(`/api/stores/${storeId}/billing/payment-methods`, {
      method: "POST",
      body: payload,
    });
  },

  async activateSubscription(
    storeId: string,
    payload: ActivateSubscriptionRequest,
  ): Promise<SubscriptionResponse> {
    return request<SubscriptionResponse>(`/api/stores/${storeId}/billing/activate`, {
      method: "POST",
      body: payload,
    });
  },

  async resolveSalesCode(code: string): Promise<SalesCodeResolveResponse> {
    return request<SalesCodeResolveResponse>(
      `/api/billing/sales-codes/${encodeURIComponent(code)}`,
    );
  },

  // --- billing (Store Settings → Billing tab; PRD PAYWALL §6, item 6) ---
  async listInvoices(storeId: string): Promise<InvoiceResponse[]> {
    const resp = await request<InvoicesListResponse>(`/api/stores/${storeId}/billing/invoices`);
    return resp.invoices ?? [];
  },

  async cancelSubscription(storeId: string): Promise<SubscriptionResponse> {
    return request<SubscriptionResponse>(`/api/stores/${storeId}/billing/cancel`, {
      method: "POST",
    });
  },

  async previewCoupon(
    storeId: string,
    payload: ApplyCouponRequest,
  ): Promise<CouponPreviewResponse> {
    return request<CouponPreviewResponse>(`/api/stores/${storeId}/billing/coupon/preview`, {
      method: "POST",
      body: payload,
    });
  },

  async applyCoupon(storeId: string, payload: ApplyCouponRequest): Promise<SubscriptionResponse> {
    return request<SubscriptionResponse>(`/api/stores/${storeId}/billing/coupon`, {
      method: "POST",
      body: payload,
    });
  },

  async previewPrepay(storeId: string, payload: PrepayRequest): Promise<PrepayPreviewResponse> {
    return request<PrepayPreviewResponse>(`/api/stores/${storeId}/billing/prepay/preview`, {
      method: "POST",
      body: payload,
    });
  },

  async purchasePrepay(storeId: string, payload: PrepayRequest): Promise<PrepayPurchaseResponse> {
    return request<PrepayPurchaseResponse>(`/api/stores/${storeId}/billing/prepay`, {
      method: "POST",
      body: payload,
    });
  },

  async removePaymentMethod(storeId: string, paymentMethodId: string): Promise<void> {
    await request<void>(`/api/stores/${storeId}/billing/payment-methods/${paymentMethodId}`, {
      method: "DELETE",
    });
  },

  // --- device orders (Shopping; paywall device slice) ---
  async previewDeviceOrder(
    storeId: string,
    payload: DeviceOrderRequest,
  ): Promise<DeviceOrderPreviewResponse> {
    return request<DeviceOrderPreviewResponse>(
      `/api/stores/${storeId}/billing/device-orders/preview`,
      { method: "POST", body: payload },
    );
  },

  async createDeviceOrder(
    storeId: string,
    payload: DeviceOrderRequest,
  ): Promise<DeviceOrderResponse> {
    return request<DeviceOrderResponse>(`/api/stores/${storeId}/billing/device-orders`, {
      method: "POST",
      body: payload,
    });
  },

  async getDeviceReturn(): Promise<DeviceReturnResponse> {
    return request<DeviceReturnResponse>("/api/billing/device-return");
  },

  async listDeviceOrders(storeId: string): Promise<DeviceOrderResponse[]> {
    const resp = await request<DeviceOrdersListResponse>(
      `/api/stores/${storeId}/billing/device-orders`,
    );
    return resp.device_orders ?? [];
  },

  // --- sales partner (Account → Partner tab; PRD PAYWALL §1.1) ---
  async getPartner(): Promise<PartnerStatusResponse> {
    return request<PartnerStatusResponse>("/api/partner");
  },

  async getPartnerContract(): Promise<PartnerContractResponse> {
    return request<PartnerContractResponse>("/api/partner/contract");
  },

  async signPartnerContract(payload: { typed_name: string }): Promise<PartnerStatusResponse> {
    return request<PartnerStatusResponse>("/api/partner/sign", {
      method: "POST",
      body: payload,
    });
  },

  async getPartnerAttributions(): Promise<PartnerAttributionsListResponse> {
    return request<PartnerAttributionsListResponse>("/api/partner/attributions");
  },
  async getPartnerStatement(): Promise<PartnerStatementResponse> {
    return request<PartnerStatementResponse>("/api/partner/statement");
  },

  // The signed copy is a binary download, not JSON — same auth header, blob body.
  // --- account wallet (Account → Wallet tab; PRD PAYWALL §6, item 7) ---
  async getWallet(): Promise<WalletResponse> {
    return request<WalletResponse>("/api/wallet");
  },
  async requestWalletPayout(): Promise<WalletPayoutResponse> {
    return request<WalletPayoutResponse>("/api/wallet/payout", { method: "POST" });
  },
  async downloadPartnerContractPdf(): Promise<Blob> {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const resp = await fetch("/api/partner/contract.pdf", { headers });
    if (!resp.ok) throw new ApiError(resp.status, `Request failed (${resp.status})`, null);
    return resp.blob();
  },

  // --- auth ---
  async login(payload: LoginRequest): Promise<LoginResponse> {
    const resp = await request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: payload,
      skipAuth: true,
    });
    setToken(resp.access_token);
    return resp;
  },

  /** Sign in — or sign up — with the ID token Google's library hands the page (ADR 0007). */
  async googleSignIn(credential: string): Promise<LoginResponse> {
    const resp = await request<LoginResponse>("/api/auth/google", {
      method: "POST",
      body: { credential },
      skipAuth: true,
    });
    setToken(resp.access_token);
    return resp;
  },

  /** Pre-sign-in config; google_client_id null = Google sign-in is off. */
  async authConfig(): Promise<AuthConfigResponse> {
    return request<AuthConfigResponse>("/api/auth/config", { skipAuth: true });
  },

  async register(payload: RegisterRequest): Promise<UserResponse> {
    return request<UserResponse>("/api/auth/register", {
      method: "POST",
      body: payload,
      skipAuth: true,
    });
  },

  async verifyRegistration(email: string, code: string): Promise<void> {
    await request<void>("/api/auth/register/verify", {
      method: "POST",
      body: { email, code },
      skipAuth: true,
    });
  },

  async resendRegistrationCode(email: string): Promise<void> {
    await request<void>("/api/auth/register/resend", {
      method: "POST",
      body: { email },
      skipAuth: true,
    });
  },

  async recoverStart(email: string, channel: "email" | "sms"): Promise<void> {
    await request<void>("/api/auth/recover/start", {
      method: "POST",
      body: { identifier: email, channel },
      skipAuth: true,
    });
  },

  async recoverComplete(email: string, code: string, newPassword: string): Promise<void> {
    await request<void>("/api/auth/recover/complete", {
      method: "POST",
      body: { identifier: email, code, new_password: newPassword },
      skipAuth: true,
    });
  },

  async me(): Promise<UserResponse> {
    return request<UserResponse>("/api/auth/me");
  },

  async updateMe(payload: UpdateMeRequest): Promise<UserResponse> {
    return request<UserResponse>("/api/auth/me", { method: "PATCH", body: payload });
  },

  async getNotificationPhone(): Promise<PhoneResponse> {
    return request<PhoneResponse>("/api/users/me/phone", { method: "GET" });
  },

  async updateNotificationPhone(payload: {
    phone: string | null;
    sms_consent: boolean;
  }): Promise<PhoneResponse> {
    return request<PhoneResponse>("/api/users/me/phone", { method: "PUT", body: payload });
  },

  // --- notification phones (cap 5; per-number verify/consent/carrier-block) ---
  async listPhones(): Promise<PhoneRowResponse[]> {
    const resp = await request<PhonesListResponse>("/api/account/phones");
    return resp.phones ?? [];
  },

  async addPhone(phone: string): Promise<PhoneRowResponse> {
    return request<PhoneRowResponse>("/api/account/phones", {
      method: "POST",
      body: { phone },
    });
  },

  async resendPhoneCode(phone: string): Promise<void> {
    await request<void>("/api/account/phones/send-code", { method: "POST", body: { phone } });
  },

  async verifyPhoneRow(phone: string, code: string): Promise<PhoneRowResponse> {
    return request<PhoneRowResponse>("/api/account/phones/verify", {
      method: "POST",
      body: { phone, code },
    });
  },

  async removePhone(phone: string): Promise<void> {
    await request<void>("/api/account/phones", { method: "DELETE", body: { phone } });
  },

  async setPhoneRowConsent(phone: string, smsConsent: boolean): Promise<PhoneRowResponse> {
    return request<PhoneRowResponse>("/api/account/phones/consent", {
      method: "PUT",
      body: { phone, sms_consent: smsConsent },
    });
  },

  async startPhoneVerify(phone: string): Promise<PhoneVerificationResponse> {
    return request<PhoneVerificationResponse>("/api/account/phone/verify/start", {
      method: "POST",
      body: { phone },
    });
  },

  async checkPhoneVerify(code: string): Promise<PhoneVerificationResponse> {
    return request<PhoneVerificationResponse>("/api/account/phone/verify/check", {
      method: "POST",
      body: { code },
    });
  },

  async changePassword(payload: {
    current_password: string;
    new_password: string;
  }): Promise<void> {
    await request<void>("/api/auth/password", { method: "POST", body: payload });
  },

  async startEmailChange(payload: { new_email: string; password: string }): Promise<void> {
    await request<void>("/api/auth/email/change/start", { method: "POST", body: payload });
  },

  async checkEmailChange(payload: {
    new_email: string;
    code: string;
    password: string;
  }): Promise<void> {
    await request<void>("/api/auth/email/change/check", { method: "POST", body: payload });
  },

  logout(): void {
    clearToken();
  },

  // --- stores ---
  async listStores(): Promise<StoreResponse[]> {
    const resp = await request<StoresListResponse>("/api/stores");
    return resp.stores;
  },

  async getStore(storeId: string): Promise<StoreResponse> {
    return request<StoreResponse>(`/api/stores/${storeId}`);
  },

  async createStore(payload: CreateStoreRequest): Promise<StoreResponse> {
    return request<StoreResponse>("/api/stores", { method: "POST", body: payload });
  },

  async updateStore(storeId: string, payload: UpdateStoreRequest): Promise<StoreResponse> {
    return request<StoreResponse>(`/api/stores/${storeId}`, { method: "PATCH", body: payload });
  },

  async deleteStore(storeId: string): Promise<void> {
    await request<void>(`/api/stores/${storeId}`, { method: "DELETE" });
  },

  async completeStoreSetup(storeId: string): Promise<StoreResponse> {
    return request<StoreResponse>(`/api/stores/${storeId}/setup-complete`, { method: "POST" });
  },

  async acknowledgeStoreEdgeKey(storeId: string): Promise<StoreResponse> {
    return request<StoreResponse>(`/api/stores/${storeId}/edge-key-acknowledged`, {
      method: "POST",
    });
  },

  // --- nvrs ---
  async listStoreNvrs(storeId: string): Promise<NvrResponse[]> {
    const resp = await request<NvrsListResponse>(`/api/stores/${storeId}/nvrs`);
    return resp.nvrs;
  },

  async createNvr(storeId: string, payload: RegisterNvrRequest): Promise<NvrResponse> {
    return request<NvrResponse>(`/api/stores/${storeId}/nvrs`, {
      method: "POST",
      body: payload,
    });
  },

  async getNvr(nvrId: string): Promise<NvrResponse> {
    return request<NvrResponse>(`/api/nvrs/${nvrId}`);
  },

  async updateNvr(nvrId: string, payload: UpdateNvrRequest): Promise<NvrResponse> {
    return request<NvrResponse>(`/api/nvrs/${nvrId}`, { method: "PATCH", body: payload });
  },

  async deleteNvr(nvrId: string): Promise<void> {
    await request<void>(`/api/nvrs/${nvrId}`, { method: "DELETE" });
  },

  // --- cameras ---
  // Camera existence/assignment is edge-authoritative (ADR 0017): rows are minted by the
  // edge's roster report; adding or re-binding cameras happens on the store's Setup Panel.
  async listStoreCameras(storeId: string): Promise<CameraResponse[]> {
    const resp = await request<CamerasListResponse>(`/api/stores/${storeId}/cameras`);
    return resp.cameras;
  },

  async getCamera(cameraId: string): Promise<CameraResponse> {
    return request<CameraResponse>(`/api/cameras/${cameraId}`);
  },

  async updateCamera(cameraId: string, payload: UpdateCameraRequest): Promise<CameraResponse> {
    return request<CameraResponse>(`/api/cameras/${cameraId}`, {
      method: "PATCH",
      body: payload,
    });
  },

  async deleteCamera(cameraId: string): Promise<void> {
    await request<void>(`/api/cameras/${cameraId}`, { method: "DELETE" });
  },

  // --- edge agents ---
  async listStoreEdgeAgents(storeId: string): Promise<EdgeAgentResponse[]> {
    const resp = await request<EdgeAgentsListResponse>(`/api/stores/${storeId}/edge-agents`);
    return resp.edge_agents;
  },

  async createEdgeAgent(
    storeId: string,
    payload: CreateEdgeAgentRequest = {},
  ): Promise<ProvisionedEdgeAgentResponse> {
    return request<ProvisionedEdgeAgentResponse>(`/api/stores/${storeId}/edge-agents`, {
      method: "POST",
      body: payload,
    });
  },

  async updateEdgeAgent(
    storeId: string,
    edgeAgentId: string,
    payload: UpdateEdgeAgentRequest,
  ): Promise<EdgeAgentResponse> {
    return request<EdgeAgentResponse>(`/api/stores/${storeId}/edge-agents/${edgeAgentId}`, {
      method: "PATCH",
      body: payload,
    });
  },

  async deleteEdgeAgent(storeId: string, edgeAgentId: string): Promise<void> {
    await request<void>(`/api/stores/${storeId}/edge-agents/${edgeAgentId}`, { method: "DELETE" });
  },

  async disableEdgeAgent(storeId: string, edgeAgentId: string): Promise<EdgeAgentResponse> {
    return request<EdgeAgentResponse>(`/api/stores/${storeId}/edge-agents/${edgeAgentId}/disable`, {
      method: "POST",
    });
  },

  async enableEdgeAgent(storeId: string, edgeAgentId: string): Promise<EdgeAgentResponse> {
    return request<EdgeAgentResponse>(`/api/stores/${storeId}/edge-agents/${edgeAgentId}/enable`, {
      method: "POST",
    });
  },

  // --- members ---
  async listMembers(storeId: string): Promise<MemberResponse[]> {
    const resp = await request<MembersListResponse>(`/api/stores/${storeId}/members`);
    return resp.members;
  },

  async addMember(storeId: string, payload: AddMemberRequest): Promise<MemberResponse> {
    return request<MemberResponse>(`/api/stores/${storeId}/members`, {
      method: "POST",
      body: payload,
    });
  },

  async updateMemberRole(
    storeId: string,
    userId: string,
    payload: UpdateMemberRoleRequest,
  ): Promise<MemberResponse> {
    return request<MemberResponse>(`/api/stores/${storeId}/members/${userId}`, {
      method: "PATCH",
      body: payload,
    });
  },

  async removeMember(storeId: string, userId: string): Promise<void> {
    await request<void>(`/api/stores/${storeId}/members/${userId}`, { method: "DELETE" });
  },

  async transferOwnership(
    storeId: string,
    payload: TransferOwnershipRequest,
  ): Promise<StoreResponse> {
    return request<StoreResponse>(`/api/stores/${storeId}/transfer-ownership`, {
      method: "POST",
      body: payload,
    });
  },

  // --- notification grants ---
  async markPeriodObserved(cameraId: string, periodId: string): Promise<void> {
    await request<void>(
      `/api/cameras/${cameraId}/periods/${encodeURIComponent(periodId)}/observed`,
      { method: "POST" },
    );
  },

  async listGrants(storeId: string): Promise<GrantResponse[]> {
    const resp = await request<GrantsListResponse>(`/api/stores/${storeId}/notification-grants`);
    return resp.grants;
  },

  async listMyGrants(storeId: string): Promise<GrantResponse[]> {
    const resp = await request<GrantsListResponse>(`/api/stores/${storeId}/notification-grants/me`);
    return resp.grants;
  },

  async putGrant(
    storeId: string,
    userId: string,
    channel: NotificationChannel,
  ): Promise<GrantResponse> {
    return request<GrantResponse>(
      `/api/stores/${storeId}/notification-grants/${userId}/${channel}`,
      { method: "PUT" },
    );
  },

  async deleteGrant(storeId: string, userId: string, channel: NotificationChannel): Promise<void> {
    await request<void>(`/api/stores/${storeId}/notification-grants/${userId}/${channel}`, {
      method: "DELETE",
    });
  },

  async setMyGrantEnabled(
    storeId: string,
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<GrantResponse> {
    return request<GrantResponse>(
      `/api/stores/${storeId}/notification-grants/me/${channel}/enabled`,
      { method: "PUT", body: { enabled } },
    );
  },

  // --- media consent ---
  async getMediaConsent(): Promise<MediaConsentResponse> {
    return request<MediaConsentResponse>("/api/users/me/media-consent");
  },

  async setMediaConsent(consent: boolean): Promise<MediaConsentResponse> {
    return request<MediaConsentResponse>("/api/users/me/media-consent", {
      method: "PUT",
      body: { consent },
    });
  },

  // --- camera intelligence (periods / alerts / sensory) ---
  async getStoreReport(
    storeId: string,
    filters: ReportFilters,
    cursor?: string,
  ): Promise<ReportResponse> {
    const extra = cursor ? { cursor } : undefined;
    return request<ReportResponse>(`/api/stores/${storeId}/report?${reportQuery(filters, extra)}`);
  },

  // A file, not JSON: same auth header, blob body. A refusal (422 over the row cap)
  // still answers JSON, and its reason is what the owner needs to see.
  async downloadStoreReportCsv(
    storeId: string,
    filters: ReportFilters,
    columns: ReportCsvColumn[],
  ): Promise<{ blob: Blob; filename: string }> {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const query = reportQuery(filters, { columns: columns.join(",") });
    const resp = await fetch(`/api/stores/${storeId}/report.csv?${query}`, { headers });
    if (resp.ok) {
      const disposition = resp.headers.get("Content-Disposition") ?? "";
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "shobdo-report.csv";
      return { blob: await resp.blob(), filename };
    }
    let body: ApiErrorBody | null = null;
    try {
      body = JSON.parse(await resp.text()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new ApiError(resp.status, body?.error ?? `Request failed (${resp.status})`, body);
  },

  async listCameraPeriods(
    cameraId: string,
    opts: EventWindow & { sort?: AlertSort } = {},
  ): Promise<MotionPeriodResponse[]> {
    const resp = await request<MotionPeriodsListResponse>(
      `/api/cameras/${cameraId}/periods${eventQuery(opts)}`,
    );
    return resp.periods ?? [];
  },

  async listCameraAlerts(
    cameraId: string,
    opts: EventWindow & { sort?: AlertSort } = {},
  ): Promise<CameraAlertResponse[]> {
    const resp = await request<CameraAlertsListResponse>(
      `/api/cameras/${cameraId}/alerts${eventQuery(opts)}`,
    );
    return resp.alerts ?? [];
  },

  async listCameraSensory(
    cameraId: string,
    opts: EventWindow & { periodId?: string } = {},
  ): Promise<SensoryLogResponse[]> {
    const resp = await request<SensoryLogsListResponse>(
      `/api/cameras/${cameraId}/sensory${eventQuery(opts)}`,
    );
    return resp.sensory ?? [];
  },

  // --- live view (HMAC view token → SSE stream) ---
  async createViewToken(cameraId: string): Promise<ViewTokenResponse> {
    return request<ViewTokenResponse>(`/api/cameras/${cameraId}/view-token`, { method: "POST" });
  },

  async createClipToken(
    cameraId: string,
    periodId: string,
    password: string,
  ): Promise<ClipTokenResponse> {
    return request<ClipTokenResponse>(`/api/cameras/${cameraId}/periods/${periodId}/clip-token`, {
      method: "POST",
      body: { password },
    });
  },

  // --- watch instructions (sanitization request lifecycle) ---
  async getWatchItems(kind: "camera" | "store", id: string): Promise<WatchItemsResponse> {
    return request<WatchItemsResponse>(`/api/${kind}s/${id}/watch-items`);
  },

  async submitWatchInstruction(
    kind: "camera" | "store",
    id: string,
    rawText: string,
  ): Promise<void> {
    await request<unknown>(`/api/${kind}s/${id}/watch-instructions`, {
      method: "POST",
      body: { raw_text: rawText },
    });
  },

  async deleteWatchItem(kind: "camera" | "store", id: string, itemId: number): Promise<void> {
    await request<void>(`/api/${kind}s/${id}/watch-items/${itemId}`, { method: "DELETE" });
  },

  async submitWatchInstructions(
    cameraId: string,
    payload: SubmitWatchInstructionsRequest,
  ): Promise<WatchInstructionsResponse> {
    return request<WatchInstructionsResponse>(`/api/cameras/${cameraId}/watch-instructions`, {
      method: "POST",
      body: payload,
    });
  },

  /** Latest watch-instructions request for the camera, or null when none exist yet. */
  async getWatchInstructions(cameraId: string): Promise<WatchInstructionsResponse | null> {
    try {
      return await request<WatchInstructionsResponse>(
        `/api/cameras/${cameraId}/watch-instructions`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },

  // --- telegram link ceremony ---
  async createTelegramLinkCode(): Promise<TelegramLinkCodeResponse> {
    return request<TelegramLinkCodeResponse>("/api/telegram/link-code", { method: "POST" });
  },
};
